"use client";
import { ShinyButton } from "@/components/magicui/shiny-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Show, UserButton, useAuth } from "@clerk/nextjs";
import { motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import RotatingUpgradeBanners from "./RotatingUpgradeBanners";
import { Logo } from "./logo";

export const HeroHeader = () => {
  const [menuState, setMenuState] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isLoaded } = useAuth();
  const pathname = usePathname();

  const navItems = [
    { name: "Home", href: "/" },
    {
      name: "AI Agents",
      href: "/ai-agents",
      dropdownItems: [
        { name: "Live Apply Agent", href: "/ai-agents/live-apply" },
        { name: "Auto Apply Agent", href: "/ai-agents/auto-apply" },
        { name: "Tailored Apply Agent", href: "/ai-agents/tailored-apply" },
        { name: "Inbox Apply Agent", href: "/ai-agents/inbox-apply" },
      ],
    },
    { name: "Job Board", href: "/jobs" },
    {
      name: "Features",
      href: "/features",
      dropdownItems: [
        {
          name: "Smart Resume Builder",
          href: "/features/smart-resume-builder",
        },
        {
          name: "Resume JobFit Scorer",
          href: "/features/resume-jobfit-scorer",
        },
        {
          name: "Cover Letter Copilot",
          href: "/features/cover-letter-copilot",
        },
        { name: "AI Job Match", href: "/features/ai-job-match" },
        { name: "Jobs Deep Research", href: "/features/jobs-deep-research" },
        { name: "Insider Connections", href: "/features/insider-connections" },
        { name: "AI Mock Interview", href: "/features/ai-mock-interview" },
        { name: "JobTracker AI", href: "/features/jobtracker-ai" },
      ],
    },
    { name: "How to Use", href: "/how-to-use" },
    {
      name: "Resources",
      href: "/resources",
      dropdownItems: [
        { name: "Blogs", href: "/blogs" },
        { name: "Career Guides", href: "/resources/career-guides" },
        { name: "Salary Insights", href: "/resources/salary-insights" },
        { name: "Layoffs Tracker", href: "/resources/layoffs-tracker" },
        { name: "Hiring Trends", href: "/resources/hiring-trends" },
        { name: "FAQ", href: "/resources/faq" },
      ],
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header>
      <nav data-state={menuState && "active"} className="fixed z-50 w-full">
        <div className={cn("mx-auto w-full bg-white py-0 upgradeBanners")}>
          <RotatingUpgradeBanners />
        </div>
        <div
          className={cn(
            "mx-auto mt-2 w-[calc(100%-32px)] md:w-full px-3 transition-all duration-300 lg:px-12",
            isScrolled &&
              "bg-background/50 max-w-6xl rounded-2xl border backdrop-blur-lg lg:px-2",
          )}
        >
          <div className="relative flex flex-wrap items-center justify-between gap-6 py-3 lg:gap-0 lg:py-2">
            <div className="flex w-full justify-between lg:w-auto">
              <Link
                href="/"
                aria-label="home"
                className="flex items-center space-x-2"
              >
                <Logo />
              </Link>

              <button
                onClick={() => setMenuState(!menuState)}
                aria-label={menuState == true ? "Close Menu" : "Open Menu"}
                className="relative z-20 -m-2.5 -mr-4 block cursor-pointer p-2.5 lg:hidden"
              >
                <Menu className="in-data-[state=active]:rotate-180 in-data-[state=active]:scale-0 in-data-[state=active]:opacity-0 m-auto size-6 duration-200" />
                <X className="in-data-[state=active]:rotate-0 in-data-[state=active]:scale-100 in-data-[state=active]:opacity-100 absolute inset-0 m-auto size-6 -rotate-180 scale-0 opacity-0 duration-200" />
              </button>
            </div>

            <div className="absolute inset-0 m-auto hidden size-fit lg:block">
              <ul className="flex gap-8 text-base">
                {navItems.map((item, index) => {
                  if (item.dropdownItems) {
                    const isDropdownActive =
                      item.dropdownItems.some((subItem) =>
                        pathname.startsWith(subItem.href),
                      ) ||
                      (item.href && pathname === item.href);

                    return (
                      <li
                        key={index}
                        className="relative flex items-center gap-1"
                      >
                        <Link
                          href={item.href || "#"}
                          className={cn(
                            "font-matter-medium duration-150 hover:scale-105 transition-all",
                            isDropdownActive
                              ? "text-foreground font-semibold"
                              : "text-muted-foreground hover:text-emerald-600",
                          )}
                        >
                          <span>{item.name}</span>
                        </Link>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              suppressHydrationWarning
                              className={cn(
                                "group p-0.5 rounded-full hover:bg-emerald-100/50 transition-colors cursor-pointer",
                                isDropdownActive
                                  ? "text-foreground"
                                  : "text-muted-foreground hover:text-emerald-600",
                              )}
                            >
                              <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            className="w-56 mt-2 bg-linear-to-br from-emerald-50/95 to-green-50/95 backdrop-blur-md border-emerald-200/50 shadow-xl shadow-emerald-500/10"
                            align="start"
                            sideOffset={8}
                          >
                            {item.dropdownItems.map((subItem, subIndex) => {
                              const isActive = pathname.startsWith(
                                subItem.href,
                              );
                              return (
                                <DropdownMenuItem key={subIndex} asChild>
                                  <Link
                                    prefetch={false}
                                    href={subItem.href}
                                    className={cn(
                                      "flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-all duration-200 cursor-pointer group",
                                      isActive
                                        ? "bg-emerald-100/80 text-emerald-800 font-semibold shadow-sm"
                                        : "text-emerald-700 hover:bg-emerald-100/60 hover:text-emerald-800 hover:translate-x-1",
                                    )}
                                  >
                                    <div className="flex items-center space-x-3">
                                      <div
                                        className={cn(
                                          "w-2 h-2 rounded-full transition-all duration-200",
                                          isActive
                                            ? "bg-emerald-500 shadow-sm"
                                            : "bg-emerald-300/50 group-hover:bg-emerald-400",
                                        )}
                                      />
                                      <span className="truncate">
                                        {subItem.name}
                                      </span>
                                    </div>
                                    {isActive && (
                                      <div className="ml-auto">
                                        <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                                      </div>
                                    )}
                                  </Link>
                                </DropdownMenuItem>
                              );
                            })}
                          </DropdownMenuContent>
                        </DropdownMenu>

                        {isDropdownActive && (
                          <motion.div
                            layoutId="activeTab"
                            className="absolute -bottom-1 left-0 right-0 h-0.5 bg-linear-to-r from-emerald-400 to-green-500 rounded-full shadow-sm"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.2 }}
                          />
                        )}
                      </li>
                    );
                  }

                  const isActive =
                    item.href === "/"
                      ? pathname === "/"
                      : item.href && pathname.startsWith(item.href);
                  return (
                    <li key={index} className="relative">
                      <Link
                        prefetch={false}
                        href={item.href || "#"}
                        className={cn(
                          "block font-matter-medium duration-150",
                          isActive
                            ? "text-foreground font-semibold"
                            : "text-muted-foreground hover:text-accent-foreground",
                        )}
                      >
                        <span>{item.name}</span>
                      </Link>
                      {isActive && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.2 }}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="bg-background in-data-[state=active]:block lg:in-data-[state=active]:flex mb-6 hidden w-full flex-wrap items-center justify-end space-y-8 rounded-3xl border p-6 shadow-2xl shadow-zinc-300/20 md:flex-nowrap lg:m-0 lg:flex lg:w-fit lg:gap-6 lg:space-y-0 lg:border-transparent lg:bg-transparent lg:p-0 lg:shadow-none dark:shadow-none dark:lg:bg-transparent">
              <div className="lg:hidden w-full">
                <ul className="space-y-1">
                  {navItems.map((item, index) => {
                    const isActive =
                      item.href === "/"
                        ? pathname === "/"
                        : item.href && pathname.startsWith(item.href);

                    if (item.dropdownItems) {
                      // Collapsible section for items with dropdowns
                      return (
                        <li key={index} className="relative">
                          <details className="group">
                            <summary
                              className={cn(
                                "flex items-center justify-between py-3 px-4 rounded-xl cursor-pointer list-none transition-all duration-200",
                                isActive
                                  ? "bg-emerald-100/80 text-emerald-800 font-semibold"
                                  : "text-slate-600 hover:bg-slate-100",
                              )}
                            >
                              <div className="flex items-center gap-3">
                                <div
                                  className={cn(
                                    "w-2 h-2 rounded-full",
                                    isActive
                                      ? "bg-emerald-500"
                                      : "bg-slate-300",
                                  )}
                                />
                                <span className="font-medium">{item.name}</span>
                              </div>
                              <ChevronDown className="h-4 w-4 transition-transform duration-300 group-open:rotate-180" />
                            </summary>
                            <div className="mt-1 ml-4 pl-4 border-l-2 border-emerald-200/50 space-y-1 pb-2">
                              {item.dropdownItems.map((subItem, subIndex) => {
                                const isSubActive = pathname.startsWith(
                                  subItem.href,
                                );
                                return (
                                  <Link
                                    key={subIndex}
                                    prefetch={false}
                                    href={subItem.href}
                                    onClick={() => setMenuState(false)}
                                    className={cn(
                                      "flex items-center gap-2 py-2.5 px-3 rounded-lg text-sm transition-all duration-200",
                                      isSubActive
                                        ? "bg-emerald-100/60 text-emerald-700 font-medium"
                                        : "text-slate-500 hover:bg-slate-50 hover:text-slate-700",
                                    )}
                                  >
                                    <div
                                      className={cn(
                                        "w-1.5 h-1.5 rounded-full",
                                        isSubActive
                                          ? "bg-emerald-500"
                                          : "bg-slate-300",
                                      )}
                                    />
                                    {subItem.name}
                                  </Link>
                                );
                              })}
                            </div>
                          </details>
                        </li>
                      );
                    }

                    // Regular nav item without dropdown
                    return (
                      <li key={index} className="relative">
                        <Link
                          prefetch={false}
                          href={item.href || "#"}
                          onClick={() => setMenuState(false)}
                          className={cn(
                            "flex items-center gap-3 py-3 px-4 rounded-xl transition-all duration-200",
                            isActive
                              ? "bg-emerald-100/80 text-emerald-800 font-semibold"
                              : "text-slate-600 hover:bg-slate-100",
                          )}
                        >
                          <div
                            className={cn(
                              "w-2 h-2 rounded-full",
                              isActive ? "bg-emerald-500" : "bg-slate-300",
                            )}
                          />
                          <span className="font-medium">{item.name}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="flex w-full flex-col space-y-3 sm:flex-row sm:gap-3 sm:space-y-0 md:w-fit">
                {isLoaded && (
                  <>
                    <Show when="signed-out">
                      <Link href="/sign-in">
                        <ShinyButton variant="light">Login</ShinyButton>
                      </Link>
                      <Link href="/sign-up">
                        <ShinyButton variant="dark">Sign Up</ShinyButton>
                      </Link>
                    </Show>
                    <Show when="signed-in">
                      <Link href="/autoapply">
                        <ShinyButton variant="dark">Dashboard</ShinyButton>
                      </Link>
                      <UserButton />
                    </Show>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};
